// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/financeiro/components/EmitirBoletoContasReceberDialog.tsx
//
//  Emissão de boleto A PARTIR DE CONTAS A RECEBER: escolhe a conta bancária
//  (com integração por API), filtra o cliente, marca os títulos em aberto e o
//  sistema registra cada boleto no banco. Nosso número, linha digitável e
//  código de barras vêm do banco — o usuário não digita nada disso. Juros,
//  multa, desconto, protesto e instruções saem da configuração da conta.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { getApiErrorMessage } from '@/lib/apiClient';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import { contasReceberService } from '@/features/financeiro/services/contasReceberService';
import { useBancoIntegracaoProvider } from '@/features/bancoIntegracao/hooks/useBancoIntegracaoProvider';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Chamado quando ao menos um boleto foi registrado (para recarregar a lista). */
  onEmitidos: () => void;
}

type Resultado = { contaReceberId: number; ok: boolean; texto: string };

const SEM_CLIENTE = 0;

function fmtValor(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtData(s: string) {
  const [a, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

export function EmitirBoletoContasReceberDialog({ open, onClose, onEmitidos }: Props) {
  const [contaId, setContaId] = useState<number | null>(null);
  const [clienteId, setClienteId] = useState<number>(SEM_CLIENTE);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [pix, setPix] = useState<'__padrao__' | 'sim' | 'nao'>('__padrao__');
  const [mensagem, setMensagem] = useState('');
  const [emitindo, setEmitindo] = useState(false);
  const [resultados, setResultados] = useState<Resultado[]>([]);

  // Reabrir começa limpo.
  useEffect(() => {
    if (!open) return;
    setSelecionados(new Set());
    setResultados([]);
  }, [open]);

  const { data: contas = [] } = useQuery({
    queryKey: ['contas-correntes-combo'],
    queryFn: () => cadastrosFinanceirosService.getContasCorrentesCombo(),
    enabled: open,
  });

  const { provider, carregando: carregandoProvider } = useBancoIntegracaoProvider(contaId);

  const titulosQuery = useQuery({
    queryKey: ['boleto-titulos-abertos', clienteId, busca],
    queryFn: () => contasReceberService.getAll({
      status: 'aberto',
      clienteId: clienteId || undefined,
      busca: busca || undefined,
      tamanhoPagina: 100,
      ordenarPor: 'vencimento',
      direcao: 'asc',
    }),
    enabled: open,
  });
  const titulos = useMemo(() => titulosQuery.data?.itens ?? [], [titulosQuery.data]);

  const selecionadosLista = titulos.filter(t => selecionados.has(t.id));
  const totalSelecionado = selecionadosLista.reduce((s, t) => s + (t.valorSaldo || t.valorReceber), 0);
  const todosMarcados = titulos.length > 0 && titulos.every(t => selecionados.has(t.id));

  const alternar = (id: number) => setSelecionados(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const alternarTodos = () =>
    setSelecionados(todosMarcados ? new Set() : new Set(titulos.map(t => t.id)));

  const podeEmitir = !!contaId && !!provider?.capabilities.cobranca && selecionadosLista.length > 0 && !emitindo;

  const emitir = async () => {
    if (!contaId || !provider) return;
    setEmitindo(true);
    setResultados([]);
    const saida: Resultado[] = [];
    // Um a um: o banco limita a taxa de inclusão e cada título tem seu retorno.
    for (const t of selecionadosLista) {
      try {
        const r = await provider.emitirCobranca(contaId, t.id, {
          mensagem: mensagem.trim() || undefined,
          comPix: pix === '__padrao__' ? null : pix === 'sim',
        });
        saida.push({ contaReceberId: t.id, ok: true, texto: `Nosso número ${r.codigoSolicitacao}` });
      } catch (e) {
        saida.push({ contaReceberId: t.id, ok: false, texto: getApiErrorMessage(e) });
      }
      setResultados([...saida]);
    }
    setEmitindo(false);
    setSelecionados(new Set(saida.filter(r => !r.ok).map(r => r.contaReceberId)));
    if (saida.some(r => r.ok)) {
      onEmitidos();
      titulosQuery.refetch();
    }
  };

  const resultadoDe = (id: number) => resultados.find(r => r.contaReceberId === id);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v && !emitindo) onClose(); }}>
      <DialogContent className="sm:max-w-[860px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Emitir boleto</DialogTitle>
          <DialogDescription>
            Escolha a conta bancária e os títulos de contas a receber. O boleto é registrado no banco;
            nosso número, linha digitável e código de barras vêm do próprio banco.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Conta bancária *</Label>
            <Select value={contaId ? String(contaId) : ''} onValueChange={v => setContaId(Number(v))}>
              <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
              <SelectContent>
                {contas.map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {contaId && !carregandoProvider && !provider && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Esta conta não tem integração de cobrança por API (configure em Contas Correntes → Integração).
              </p>
            )}
            {provider && (
              <p className="text-xs text-muted-foreground">Registro via API: {provider.nome}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Cliente</Label>
            <PessoaSearchCombobox
              value={clienteId || undefined}
              onChange={id => { setClienteId(id); setSelecionados(new Set()); }}
              filter="cliente"
              placeholder="Todos os clientes"
            />
          </div>
          <div className="space-y-1">
            <Label>Documento</Label>
            <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nº do documento ou duplicata" />
          </div>
          <div className="space-y-1">
            <Label>QR Code PIX no boleto</Label>
            <Select value={pix} onValueChange={v => setPix(v as typeof pix)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__padrao__">Padrão do contrato</SelectItem>
                <SelectItem value="sim">Incluir</SelectItem>
                <SelectItem value="nao">Não incluir</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={todosMarcados} onCheckedChange={alternarTodos} aria-label="Marcar todos" />
                </TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {titulosQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Carregando títulos…
                </TableCell></TableRow>
              ) : titulos.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  Nenhum título em aberto para este filtro.
                </TableCell></TableRow>
              ) : titulos.map(t => {
                const r = resultadoDe(t.id);
                const vencido = t.dtVencimento.slice(0, 10) < new Date().toISOString().slice(0, 10);
                return (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => alternar(t.id)}>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Checkbox checked={selecionados.has(t.id)} onCheckedChange={() => alternar(t.id)} />
                    </TableCell>
                    <TableCell>{t.clienteNome ?? `Cliente ${t.clienteId}`}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.numDocumento || t.duplicata || '—'}
                      {t.qtdParcelas > 1 && ` (${t.nroParcela}/${t.qtdParcelas})`}
                    </TableCell>
                    <TableCell>
                      {fmtData(t.dtVencimento)}
                      {vencido && <Badge variant="outline" className="ml-2">vencido</Badge>}
                    </TableCell>
                    <TableCell className="text-right">{fmtValor(t.valorSaldo || t.valorReceber)}</TableCell>
                    <TableCell className="text-xs">
                      {r && (r.ok
                        ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{r.texto}</span>
                        : <span className="text-destructive flex items-center gap-1"><XCircle className="h-3 w-3 shrink-0" />{r.texto}</span>)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="space-y-1">
          <Label>Mensagem no boleto (opcional)</Label>
          <Input value={mensagem} onChange={e => setMensagem(e.target.value)}
            placeholder="Em branco usa a Descrição do Boleto da conta" />
          <p className="text-xs text-muted-foreground">
            Juros, multa, desconto e protesto vêm da configuração da conta bancária. Título vencido sai com
            vencimento para hoje.
          </p>
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {selecionadosLista.length} título(s) — {fmtValor(totalSelecionado)}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={emitindo}>Fechar</Button>
            <Button onClick={emitir} disabled={!podeEmitir}>
              {emitindo && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Emitir {selecionadosLista.length || ''} boleto(s)
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
